import React, { useState } from "react";
import {
  comparableHistory,
  comparisonMetrics,
  metricDifference,
  comparisonSources,
  contextualMetrics,
} from "../helpers/teamWorkspace";
export default function TeamSourceComparison({
  observations,
  teamId,
  en = false,
  data,
  locked = false,
}) {
  const t = (de, eng) => (en ? eng : de);
  const labels = {
    "settings-result":t("Ergebnismodell · Settings", "Results model · settings"),
    "settings-statistics":t("Statistikmodell · Settings", "Statistics model · settings"),
    previous:t("Vorsaison", "Previous season"),
    should: "Should Goals",
    result: t("Ergebnismodell · erwartet", "Results model · expected"),
    real: t("Reale Ergebnisse", "Actual results"),
    statistics: t("Statistikmodell · erwartet", "Statistics model · expected"),
    market: t("Marktrückrechnung", "Market inversion"),
  };
  const [left, setLeft] = useState("should"),
    [right, setRight] = useState("result"),
    [recent, setRecent] = useState(0),
    [venue, setVenue] = useState("all"),
    [perGame, setPerGame] = useState(true),
    [common, setCommon] = useState(true);
  const isContext = source => source === "previous" || source.startsWith("settings-");
  const crossPeriod = isContext(left) || isContext(right);
  const pool = comparableHistory(observations, teamId, [left, right].filter(s=>!isContext(s)), {
    recent,
    venue,
  });
  const games = common && !crossPeriod ? pool.common : pool.selected,
    a = isContext(left)?contextualMetrics(data,teamId,left,venue,perGame):comparisonMetrics(games, teamId, left, perGame),
    b = isContext(right)?contextualMetrics(data,teamId,right,venue,perGame):comparisonMetrics(games, teamId, right, perGame);
  for (const [source,m] of [[left,a],[right,b]]) for (const key of ["win","draw","loss"]) m[key+"FairOdds"] = !["real","previous"].includes(source) && m[key+"Probability"]>0 ? 1/m[key+"Probability"] : null;
  const fmt = (n) =>
    typeof n === "number" && Number.isFinite(n)
      ? n.toLocaleString(en ? "en-GB" : "de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "—";
  const metrics = [
    ["goalsFor", t("Tore für", "Goals for")],
    ["goalsAgainst", t("Tore gegen", "Goals against")],
    ["goalTotal", perGame ? t("Torschnitt", "Goals per match") : t("Torsumme", "Goal total")],
    ["goalDifference", t("Tordifferenz", "Goal difference")],
    ["quotient", t("Torquotient", "Goal ratio")],
    ["wins", t("Siege", "Wins")],
    ["draws", t("Remis", "Draws")],
    ["losses", t("Niederlagen", "Losses")],
    ["points", perGame ? t("Punkte pro Spiel", "Points per match") : t("Punkte", "Points")],
    ["winProbability",t("Sieg · Anteil/Wahrscheinlichkeit", "Win · frequency/probability")],
    ["drawProbability",t("Remis · Anteil/Wahrscheinlichkeit", "Draw · frequency/probability")],
    ["lossProbability",t("Niederlage · Anteil/Wahrscheinlichkeit", "Loss · frequency/probability")],
    ["winFairOdds",t("Sieg · faire Quote", "Win · fair odds")],
    ["drawFairOdds",t("Remis · faire Quote", "Draw · fair odds")],
    ["lossFairOdds",t("Niederlage · faire Quote", "Loss · fair odds")],
  ];
  const select = (value, set, label) => (
    <label>
      {label}{" "}
      <select
        aria-label={label}
        disabled={locked}
        value={value}
        onChange={(e) => set(e.target.value)}
      >
        {[...comparisonSources,"settings-result","settings-statistics","previous"].map((k) => (
          <option key={k} value={k}>
            {labels[k]}
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <section className="ta-section ta-source-comparison">
      <h3>
        {t(
          "Leistung und Erwartungen vergleichen",
          "Compare performance and expectations"
        )}
      </h3>
      <div className="ta-controls">
        {select(left, setLeft, t("Quelle A", "Source A"))}
        {select(right, setRight, t("Quelle B", "Source B"))}
        <label>
          {t("Zeitraum", "Period")}{" "}
          <select
            disabled={locked}
            value={recent}
            onChange={(e) => setRecent(Number(e.target.value))}
          >
            <option value={0}>{t("Saison", "Season")}</option>
            {[4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>
                {t("Letzte", "Last")} {n}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("Spielort", "Venue")}{" "}
          <select disabled={locked} value={venue} onChange={(e) => setVenue(e.target.value)}>
            {[
              ["all", t("Gesamt", "All")],
              ["home", t("Heim", "Home")],
              ["away", t("Auswärts", "Away")],
            ].map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label>
          <input
            type="checkbox"
            disabled={locked}
            checked={perGame}
            onChange={(e) => setPerGame(e.target.checked)}
          />
          {t("Pro Spiel", "Per match")}
        </label>
        <label>
          <input
            type="checkbox"
            disabled={locked || crossPeriod}
            checked={common && !crossPeriod}
            onChange={(e) => setCommon(e.target.checked)}
          />
          {t("Gemeinsame Spielmenge", "Common matches")}
        </label>
      </div>
      {crossPeriod && <p role="status">{t("Perioden-/Settingsvergleich, keine gemeinsame Spielmenge. Settings haben keine historischen Bilanzen oder Summen; ihre Torwerte sind nur pro Spiel vergleichbar.","Period/settings comparison, not a common match cohort. Settings have no historical outcomes or totals; their goal values are comparable only per match.")} {a.period || ''} {b.period || ''}</p>}
      <p className="ta-source-note">
        {t("Abdeckung", "Coverage")}: {a.count ?? "—"}/{isContext(left)?"—":pool.selected.length} ·{" "}
        {b.count ?? "—"}/{isContext(right)?"—":pool.selected.length}.{" "}
        {t("Bilanzgrundlage", "Outcome coverage")}: {a.outcomes?.count || 0}/
        {games.length} · {b.outcomes?.count || 0}/{games.length}.{" "}
        {pool.selected.at(-1)?.kickoff?.slice(0, 10)} –{" "}
        {pool.selected[0]?.kickoff?.slice(0, 10)}.{" "}
        {t(
          "Abweichung = A minus B; ein positives Vorzeichen ist nicht automatisch besser.",
          "Difference = A minus B; a positive sign does not automatically mean better."
        )}
      </p>
      {!common && pool.common.length !== pool.selected.length && (
        <p role="status">
          {t(
            "Unterschiedliche Spielmengen – keine direkte Bewertung.",
            "Different match coverage — no direct assessment."
          )}
        </p>
      )}
      <p className="ta-source-note">{t("Erwartet: gespeicherte Modellwerte für die ausgewählten Spiele. Reale Ergebnisse: tatsächlich erzielte Werte.", "Expected: stored model values for the selected matches. Actual results: observed values.")}</p>
      <div className="ta-scroll">
        <table className="ta-source-table"><colgroup><col className="ta-source-metric"/><col/><col/><col className="ta-source-delta"/></colgroup>
          <thead>
            <tr>
              <th>{t("Kennzahl", "Metric")}</th>
              <th>{labels[left]}</th>
              <th>{labels[right]}</th>
              <th>Δ A − B</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map(([key, label]) => {
              const outcome = ["wins", "draws", "losses", "points"].includes(key) || /Probability|FairOdds/.test(key);
              const matched =
                !outcome ||
                (a.outcomes?.count === games.length &&
                  b.outcomes?.count === games.length);
              const d =
                common && !crossPeriod && matched ? metricDifference(a[key], b[key]) : crossPeriod && perGame ? metricDifference(a[key], b[key]) : null;
              return (
                <tr key={key}>
                  <th>{label}</th>
                  <td>{fmt(a[key])}</td>
                  <td>{fmt(b[key])}</td>
                  <td>
                    {d > 0 ? "+" : ""}
                    {fmt(d)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <small>
        {t(
          "Should Goals beschreiben statistisch gemessene Leistung. Nicht ableitbare Werte bleiben leer. Torquotienten sind unabhängig von der Summen-/Schnittauswahl.",
          "Should Goals describe statistically measured performance. Non-derivable values remain blank. Goal ratios do not depend on totals/per-match selection."
        )}
      </small>
    </section>
  );
}
